import { ManualInterventionStatus } from '@fruitful/investment-management';
import { AnalyticsUserEvent } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { PieChart } from 'react-native-gifted-charts';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChartLineIcon,
    CheckIcon,
    CreditCardIcon,
    FruitfulAccountIcon,
    H6Regular,
    Icon,
    type IconType,
    isNonMobileWeb,
    MoneyMapIcon,
    NavigationRow,
    RowList,
    ScreenContainer,
    Spinner,
    SplitIcon,
    TextRow,
    useThemeColors,
    View,
    YStack,
} from 'smoothie';

import { KYCVerificationStatus } from '../../../banking/types/BankAccount';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { getPartnerDisplayFirstName } from '../../../common/utils/userUtils';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { useGetUserInvestmentProfile } from '../../../investment-profile/hooks/useGetUserInvestmentProfile';
import { track } from '../../../logger/Logger';
import { useMoneyMapWorkflow } from '../../hooks/useMoneyMapWorkflow';
import {
    type ApexAccountsStepCompleteness,
    type CardAccountsStepCompleteness,
    type CashAccountsStepCompleteness,
    type GoWorkflowStep,
    GoWorkflowStepType,
} from '../../types/GoWorkflow';

const CompletedCheckIcon = () => (
    <View
        backgroundColor="$green900"
        width={26}
        height={26}
        borderRadius={16}
        alignItems="center"
        justifyContent="center"
    >
        <Icon icon={CheckIcon} color="$white" size={14} />
    </View>
);

const StepBlockedTile = ({ message }: { message: string }) => {
    return (
        <View
            paddingVertical={12}
            paddingHorizontal={16}
            backgroundColor="$gray200"
        >
            <View
                backgroundColor="$orange400"
                borderRadius={16}
                paddingVertical={12}
                paddingHorizontal={16}
            >
                <BodySmallRegular>{message}</BodySmallRegular>
            </View>
        </View>
    );
};

const StepTile = ({
    step,
    title,
    label,
    iconLeft,
}: {
    step: GoWorkflowStep;
    title: string;
    label: string;
    iconLeft: IconType;
}) => {
    const user = useAuthedUser();
    const { nextStepToComplete } = useMoneyMapWorkflow();
    const isNextStepToComplete = step.type === nextStepToComplete?.type;
    const markAsCompleted = step.completeness.isCompleteForMember;
    let isInteractive =
        isNextStepToComplete && step.completeness.isEnabled && !markAsCompleted;

    if (
        [
            GoWorkflowStepType.CASH_ACCOUNTS,
            GoWorkflowStepType.CARD_ACCOUNTS,
        ].includes(step.type) &&
        user.syncteraKycStatus != null &&
        user.syncteraKycStatus !== KYCVerificationStatus.ACCEPTED
    ) {
        // if KYC status is anything other than null or 'accepted', these steps are not interactive
        isInteractive = false;
    }

    return isInteractive ? (
        <NavigationRow
            title={title}
            subtitle={label}
            leftIcon={iconLeft}
            onPress={() => {
                track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_STEP_TILE, {
                    step: step.type,
                });
                router.push(step.url);
            }}
        />
    ) : (
        <TextRow
            title={title}
            subtitle={label}
            leftIcon={iconLeft}
            secondaryText=""
            leftContentOverride={
                markAsCompleted ? <CompletedCheckIcon /> : undefined
            }
        />
    );
};

const isAccountInvitationsOnly = (
    step:
        | CashAccountsStepCompleteness
        | CardAccountsStepCompleteness
        | ApexAccountsStepCompleteness,
) => {
    const memberHasAccountsToAccept =
        step.memberJointAccountsToAccept.length > 0;
    const memberHasAccountsToOpen =
        step.memberSoloAccountsToOpen.length > 0 ||
        step.jointAccountsToOpen.length > 0;

    return memberHasAccountsToAccept && !memberHasAccountsToOpen;
};

const CashAccountsStepTile = ({ step }: { step: GoWorkflowStep }) => {
    const { moneyMapCompleteness } = useMoneyMapWorkflow();
    const { cashAccountsStep } = moneyMapCompleteness;
    const user = useAuthedUser();

    const label = isAccountInvitationsOnly(cashAccountsStep)
        ? 'Join your Fruitful Cash Accounts'
        : 'Open your Fruitful Cash Accounts';

    const showKycBlockedTile =
        user.syncteraKycStatus != null &&
        [
            KYCVerificationStatus.REJECTED,
            KYCVerificationStatus.VENDOR_ERROR,
        ].includes(user.syncteraKycStatus);

    const showKycPendingTile =
        !showKycBlockedTile &&
        user.syncteraKycStatus != null &&
        user.syncteraKycStatus !== KYCVerificationStatus.ACCEPTED;

    return (
        <>
            <StepTile
                step={step}
                title="Fruitful Cash Accounts"
                label={label}
                iconLeft={FruitfulAccountIcon}
            />
            {showKycBlockedTile && (
                <StepBlockedTile message="We're unable to open accounts. Please contact our Hospitality team for more info at support@fruitful.com" />
            )}
            {showKycPendingTile && (
                <StepBlockedTile message="We're reviewing your information. A Fruitful team member may reach out for additional information" />
            )}
        </>
    );
};

const ApexAccountsStepTile = ({ step }: { step: GoWorkflowStep }) => {
    const user = useAuthedUser();
    const { moneyMapCompleteness } = useMoneyMapWorkflow();
    const { apexAccountsStep } = moneyMapCompleteness;
    const { data: investmentProfile } = useGetUserInvestmentProfile({
        userId: user?.id ?? '',
    });

    if (!apexAccountsStep) {
        return null;
    }

    const isManualInterventionRequired =
        investmentProfile?.manualInterventionStatus ===
        ManualInterventionStatus.REQUIRED;
    const areAllAccountsInGoodStanding =
        apexAccountsStep.areAllAccountsInGoodStanding;
    const areImAccountsBlocked =
        !areAllAccountsInGoodStanding || isManualInterventionRequired;

    const label = isAccountInvitationsOnly(apexAccountsStep)
        ? 'Join your Fruitful Investment Accounts'
        : 'Open your Fruitful Investment Accounts';

    return (
        <>
            <StepTile
                step={step}
                title="Fruitful Investment Accounts"
                label={label}
                iconLeft={ChartLineIcon}
            />
            {areImAccountsBlocked && (
                <StepBlockedTile message="Your investment accounts are pending approval. A Fruitful team member may reach out for additional information." />
            )}
        </>
    );
};

const IncomeSplitStepTile = ({
    step,
    label,
}: {
    step: GoWorkflowStep;
    label: string;
}) => {
    const user = useAuthedUser();
    const partnerName = getPartnerDisplayFirstName(
        user.partnerMember?.firstName,
        'your partner',
    );

    const { moneyMapCompleteness, nextStepToComplete, workflowSteps } =
        useMoneyMapWorkflow();

    const trueNextStep =
        // CMM_IM is an interstitial screen. Look past it for the real next
        // step without requiring that step to be enabled: income split is
        // disabled while we wait on the partner, and that's exactly when we
        // want to show the waiting message.
        nextStepToComplete?.type === GoWorkflowStepType.CMM_IM
            ? workflowSteps.find(
                  (s) =>
                      s.index > nextStepToComplete.index &&
                      !s.completeness.isCompleteForMember,
              )
            : nextStepToComplete;

    const { cashAccountsStep, cardAccountsStep, apexAccountsStep } =
        moneyMapCompleteness;
    const isNextStepToComplete = step.type === trueNextStep?.type;
    const areAllAccountsInGoodStanding =
        apexAccountsStep?.areAllAccountsInGoodStanding;

    const isIncomeSplitWaiting =
        isNextStepToComplete &&
        areAllAccountsInGoodStanding &&
        (!cashAccountsStep.isCompleteForMembership ||
            !cardAccountsStep.isCompleteForMembership ||
            !apexAccountsStep?.isCompleteForMembership);

    const countPartnerAccountsToOpen =
        cashAccountsStep.partnerSoloAccountsToOpen.length +
        cardAccountsStep.partnerSoloAccountsToOpen.length +
        (apexAccountsStep?.partnerSoloAccountsToOpen.length ?? 0);

    const countPartnerAccountsToAccept =
        cashAccountsStep.partnerJointAccountsToAccept.length +
        cardAccountsStep.partnerJointAccountsToAccept.length +
        (apexAccountsStep?.partnerJointAccountsToAccept.length ?? 0);

    const waitingReasons = [
        countPartnerAccountsToAccept > 0
            ? `join your joint ${countPartnerAccountsToAccept === 1 ? 'account' : 'accounts'}`
            : null,
        countPartnerAccountsToOpen > 0
            ? `open their individual ${countPartnerAccountsToOpen === 1 ? 'account' : 'accounts'}`
            : null,
    ];
    const reasons = waitingReasons.filter((r) => r != null).join(' and ');
    const waitingText = `Waiting for ${partnerName} to ${reasons}.`;

    return (
        <>
            <StepTile
                step={step}
                title="Income Split"
                label={label}
                iconLeft={() => (
                    <View rotate="180deg">
                        <SplitIcon />
                    </View>
                )}
            />
            {isIncomeSplitWaiting && <StepBlockedTile message={waitingText} />}
        </>
    );
};

const WorkflowStepProgressSpeedometer = () => {
    const { workflowSteps } = useMoneyMapWorkflow();
    const { getColor } = useThemeColors();

    const totalStepCount = workflowSteps?.length ?? 0;
    const completedStepCount =
        workflowSteps?.filter((s) => s.completeness.isCompleteForMember)
            .length ?? 0;

    const percentage = Math.min(completedStepCount / totalStepCount, 1);
    /**
     * The progress chart for this page is a "speedometer," thus, we only use the top 75%
     * of the donut. Here, convert our percentage to "out of 75" in order to properly
     * render the progress.
     */
    const percentageOf75 = percentage * 0.75;

    const primaryGreen = getColor('$green1000');
    const ringBackground = getColor('$gray400');

    return (
        <View width={80} height={80}>
            <PieChart
                strokeWidth={0}
                radius={40}
                innerRadius={35}
                initialAngle={(5 / 4) * Math.PI}
                data={[
                    { color: primaryGreen, value: percentageOf75 },
                    { color: ringBackground, value: 0.75 - percentageOf75 },
                    { color: 'transparent', value: 0.25 },
                ]}
                centerLabelComponent={() => (
                    <H6Regular>
                        {completedStepCount}/{totalStepCount}
                    </H6Regular>
                )}
                labelsPosition="outward"
                donut={true}
            />
        </View>
    );
};

const StepsList = ({ workflowSteps }: { workflowSteps: GoWorkflowStep[] }) => {
    return (
        <RowList>
            {workflowSteps.map((step) => {
                switch (step.type) {
                    case GoWorkflowStepType.MONEY_MAP:
                        return (
                            <StepTile
                                key={step.type}
                                step={step}
                                title="Money Map"
                                label="Review your Money Map"
                                iconLeft={MoneyMapIcon}
                            />
                        );
                    case GoWorkflowStepType.CASH_ACCOUNTS:
                        return (
                            <CashAccountsStepTile key={step.type} step={step} />
                        );
                    case GoWorkflowStepType.CARD_ACCOUNTS:
                        return (
                            <StepTile
                                key={step.type}
                                step={step}
                                title="Fruitful Card Accounts"
                                label="Open your Fruitful Card Accounts"
                                iconLeft={CreditCardIcon}
                            />
                        );
                    case GoWorkflowStepType.APEX_ACCOUNTS:
                        return (
                            <ApexAccountsStepTile key={step.type} step={step} />
                        );
                    case GoWorkflowStepType.CMM_IM:
                        return (
                            <StepTile
                                key={step.type}
                                step={step}
                                title="Fruitful Investment Accounts"
                                label="Learn about investment accounts"
                                iconLeft={ChartLineIcon}
                            />
                        );
                    case GoWorkflowStepType.INCOME_SPLIT:
                        return (
                            <IncomeSplitStepTile
                                key={step.type}
                                step={step}
                                label="Turn on Income Split"
                            />
                        );
                    default:
                        return null;
                }
            })}
        </RowList>
    );
};

const Start = () => {
    const { from } = useLocalSearchParams<{ from?: string }>();
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Roadmap/Go/Start');

    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);

    const {
        isLoadingCompleteness,
        workflowSteps,
        nextStepToComplete,
        isWorkflowCompleted,
    } = useMoneyMapWorkflow();

    // Default / uncommon scenarios: nextStepToComplete is null or the step is disabled
    let buttonLabel = 'Continue';
    let isDisabled = true;
    let buttonOnPress = () => {};

    // Expected / normal scenarios: we have a next step that is not null and is enabled
    if (nextStepToComplete != null) {
        if (nextStepToComplete.index === 0) buttonLabel = 'Get started';

        const isComplete = nextStepToComplete.completeness.isCompleteForMember;
        const isEnabled = nextStepToComplete.completeness.isEnabled;

        if (isEnabled && !isComplete) {
            isDisabled = false;
            buttonOnPress = () => {
                track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_START_CTA, {
                    mode: 'next-step',
                });
                router.push(nextStepToComplete.url);
            };
        }
    }

    // Completed scenario - not expected for the user to see this, but just in case
    if (isWorkflowCompleted) {
        isDisabled = false;
        buttonLabel = 'Done';
        buttonOnPress = () => {
            track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_START_CTA, {
                mode: 'workflow-complete',
            });
            // GOYO: completing the Go button flow returns the member to Home
            // rather than the Money System tab.
            router.push(goyoEnabled ? '/home' : '/home/roadmap');
        };
    }

    return (
        <ScreenContainer
            title="Money Map"
            hasBackButton
            onBack={
                from === 'onboarding'
                    ? () => {
                          router.navigate('/onboarding/review-numbers');
                      }
                    : undefined
            }
        >
            <YStack justifyContent="space-between">
                <YStack gap={24}>
                    <YStack alignItems="center" gap={8}>
                        <WorkflowStepProgressSpeedometer />

                        <H6Regular>Set up your Money Map</H6Regular>
                        <BodyMediumRegular textAlign="center">
                            A supportive money system that keeps you on track
                            effortlessly.
                        </BodyMediumRegular>
                    </YStack>

                    {isLoadingCompleteness ? (
                        <Spinner height={300} />
                    ) : (
                        <StepsList workflowSteps={workflowSteps} />
                    )}
                </YStack>

                {!isLoadingCompleteness && (
                    <View
                        width="100%"
                        enterStyle={{ opacity: 0 }}
                        marginTop={isNonMobileWeb ? 100 : 24}
                    >
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            disabled={isDisabled}
                            text={buttonLabel}
                            onPress={buttonOnPress}
                        />
                    </View>
                )}
            </YStack>
        </ScreenContainer>
    );
};

export default Start;
